/* eslint-disable react-native/no-color-literals */
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import { colors } from "@constants/theme";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import {
  getTodaysCrateDeliveries,
  getTodaysCrateDeliveriesV5,
} from "@services/api";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useSelector } from "react-redux";
import ListEmptyComponent from "../components/ListEmptyComponent";
import DeliveryCard from "./DeliveryCard";
import NoDeliveryCard from "./NoDeliveryCard";
import ReturnCard from "./ReturnCard";
import DeliveryCodeModal from "./DeliveryCodeModal";
import InfoTooltipModal from "../components/InfoTooltipModal";
import PaymentCard from "./PaymentCard";
import PaymentDetailModal from "./PaymentDetailModal";
import SkuDeliveryCard from "./SkuDeliveryCard";
import { useWebSocketEvents } from "../../../hooks/useWebSocketEvents";

const PATH = "screens/MyDeliveries/DeliveryReturn/OverView";

function ReturnProductInfoCardAndPaymentCard({ ledger, deliveryState }) {
  return deliveryState !== "Vacationing" ? (
    <PaymentCard ledger={ledger} showViewDetailsBtn={true} />
  ) : null;
}

const SectionHeader = () => {
  return (
    <View style={styles.sectionHeaderContainer}>
      <View style={styles.dashedLine} />
      <View style={styles.sectionHeaderTextContainer}>
        <Text style={styles.sectionHeaderText}>
          {t(`${PATH}/sectionHeader`)}
        </Text>
      </View>
      <View style={styles.dashedLine} />
    </View>
  );
};

const UnassignedSectionHeader = ({ count }) => {
  return (
    <View style={styles.sectionHeaderContainer}>
      <View style={styles.dashedLine} />
      <View style={styles.unassignedPillContainer}>
        <Text style={styles.unassignedPillText}>
          {t(`${PATH}/unassignedSectionHeader`, { count })}
        </Text>
      </View>
      <View style={styles.dashedLine} />
    </View>
  );
};

function Overview() {
  const isFocused = useIsFocused();
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deliveryState, setDeliveryState] = useState();
  const [visible, setVisible] = useState(false);
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [paymentDetailModalVisible, setPaymentDetailModalVisible] =
    useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState({});
  const [spokeDeliveryReturnDetails, setSpokeDeliveryReturnDetails] =
    useState(null);

  const { features = {} } = useSelector(({ configReducer }) => configReducer);
  const isRouteLevelOverview = Boolean(features?.route_level_overview_enabled);

  const { lastMessage } = useWebSocketEvents({});

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const event = JSON.parse(lastMessage.data);
        if (event.type === "REFRESH_OVERVIEW_PAGE") {
          fetchData();
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  const fetchData = async () => {
    try {
      const { data = {} } = isRouteLevelOverview
        ? await getTodaysCrateDeliveriesV5()
        : await getTodaysCrateDeliveries();
      if (data?.state === "Vacationing") {
        setDeliveryState(data?.state);
        setDeliveriesData(data);
        setIsLoading(false);
        return;
      }
      setDeliveriesData(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]),
  );

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const sortUpdates = useMemo(
    () =>
      function () {
        if (deliveryState === "Vacationing") {
          return [];
        }
        const result = [];
        const routeEntries = isRouteLevelOverview
          ? deliveriesData?.routeDetails
          : deliveriesData?.spokeDetails;
        (routeEntries || []).forEach((entry) => {
          const normalizedEntry = isRouteLevelOverview
            ? {
                ...entry,
                spokeName: entry?.spoke_name,
                routeId: entry?.route_id,
              }
            : entry;
          if (result.length === 0) {
            result.push(normalizedEntry);
          } else {
            result.push({ type: "sectionHeader" });
            result.push(normalizedEntry);
          }
        });
        if (isRouteLevelOverview && deliveriesData?.unassignedDetails?.length) {
          if (result.length > 0) {
            result.push({ type: "sectionHeader" });
          }
          result.push({
            type: "unassignedSectionHeader",
            count: deliveriesData.unassignedDetails.length,
          });
          deliveriesData.unassignedDetails.forEach((entry, index) => {
            if (index > 0) {
              result.push({ type: "sectionHeader" });
            }
            result.push({ ...entry, spokeName: entry?.spoke_name });
          });
        }
        if (deliveriesData?.skuDeliveriesDetails?.length) {
          if (result.length > 0) {
            result.push({ type: "sectionHeader" });
          }
          deliveriesData.skuDeliveriesDetails.forEach((skuDelivery, index) => {
            if (index > 0) {
              result.push({ type: "sectionHeader" });
            }
            result.push(skuDelivery);
          });
        }
        return result;
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveriesData, isRouteLevelOverview],
  );

  const renderItem = useCallback(
    ({ item }) => {
      if (item?.type === "sectionHeader") {
        return (
          <View key={item.type}>
            <SectionHeader />
          </View>
        );
      } else if (item?.type === "unassignedSectionHeader") {
        return (
          <View key={item.type}>
            <UnassignedSectionHeader count={item.count} />
          </View>
        );
      } else if (item?.type === "SKU_DELIVERIES") {
        return (
          <SkuDeliveryCard
            skuDelivery={item}
            setInfoTooltipVisible={setInfoTooltipVisible}
            setInfoTooltipData={setInfoTooltipData}
          />
        );
      } else {
        return (
          <>
            {item?.deliveryDetails ? (
              <DeliveryCard
                deliveryDetails={item?.deliveryDetails}
                deliveryBoyInfo={item?.deliveryDetails?.deliveryBoyInfo}
                returnDetails={item.returnDetails}
                spokeName={item?.spokeName}
                routeId={item?.routeId}
                deliveriesData={deliveriesData}
                setModalVisible={setVisible}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
                setSpokeDeliveryReturnDetails={setSpokeDeliveryReturnDetails}
                fetchData={fetchData}
                setPaymentDetailModalVisible={setPaymentDetailModalVisible}
                ledger={deliveriesData?.ledger}
              />
            ) : (
              <NoDeliveryCard />
            )}
            {item.returnDetails ? (
              <ReturnCard
                returnDetails={item?.returnDetails}
                deliveryBoyInfo={item?.returnDetails?.deliveryBoyInfo}
                date={item?.deliveryDetails?.date}
                spokeName={item?.spokeName}
                routeId={item?.routeId}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
                setModalVisible={setVisible}
                deliveriesData={deliveriesData}
                setSpokeDeliveryReturnDetails={setSpokeDeliveryReturnDetails}
              />
            ) : (
              <></>
            )}
          </>
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveriesData],
  );

  const keyExtractor = (item, index) => `${item?.date}-${index}`;

  const deliverySpokeName =
    (deliveriesData?.spokeDetails &&
      deliveriesData?.spokeDetails[0]?.spokeName) ||
    deliveriesData?.routeDetails?.[0]?.spoke_name ||
    deliveriesData?.unassignedDetails?.[0]?.spoke_name ||
    "";

  return (
    <View style={styles.container}>
      <InfoTooltipModal
        visible={infoTooltipVisible}
        setVisible={setInfoTooltipVisible}
        data={infoTooltipData}
      />
      <DeliveryCodeModal
        visible={visible}
        setVisible={setVisible}
        spokeDeliveryReturnDetails={spokeDeliveryReturnDetails}
      />
      <PaymentDetailModal
        showModal={paymentDetailModalVisible}
        setShowModal={setPaymentDetailModalVisible}
        spoke_name={deliverySpokeName}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={sortUpdates()}
          renderItem={renderItem}
          ListHeaderComponent={
            <ReturnProductInfoCardAndPaymentCard
              ledger={deliveriesData?.ledger}
              data={deliveriesData?.spokeDetails}
              deliveryState={deliveryState}
            />
          }
          ListEmptyComponent={
            <ListEmptyComponent
              deliveryState={deliveryState}
              ledger={deliveriesData?.ledger}
            />
          }
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.listContainer}
          onRefresh={onRefresh}
          refreshing={isRefreshing}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  listContainer: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  dashedLine: {
    flex: 1,
    opacity: 0.7,
    height: 1,
    borderWidth: 0.5,
    borderColor: colors.divider,
    borderStyle: "dashed",
    borderRadius: 1,
  },
  sectionHeaderTextContainer: {
    backgroundColor: "#EEEEEE",
    alignSelf: "center",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 40,
  },
  sectionHeaderText: {
    fontSize: 11,
    lineHeight: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
  },
  unassignedPillContainer: {
    backgroundColor: colors.veryLightOrange,
    alignSelf: "center",
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 40,
    borderWidth: 1,
    borderColor: colors.plain_amber,
  },
  unassignedPillText: {
    fontSize: 11,
    lineHeight: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.plain_amber,
  },
});

export default Overview;
